/**
 * CR-6282: cross-tab session bootstrap over BroadcastChannel.
 *
 * Auth tokens live only in sessionStorage (pentest remediation — see
 * token-manager.ts). sessionStorage is NOT shared with a new tab opened via a
 * dashboard drill-down PO / GRN hyperlink (``target="_blank"``), so that tab
 * starts with an empty session and ProtectedRoute bounces it to /login — the
 * user perceives a forced sign-out.
 *
 * BroadcastChannel lets a freshly-opened tab pull the LIVE session from an
 * already-open, authenticated tab. It is in-memory and same-origin only; nothing
 * is written to localStorage, so the token is never persisted at rest and the
 * pentest posture is preserved (an XSS on this origin could already read
 * sessionStorage directly, so this adds no new exposure).
 */
import { TokenManager } from './token-manager';

const CHANNEL_NAME = 'helix_session_sync';

type SyncMessage =
  | { type: 'REQUEST_SESSION' }
  | { type: 'SESSION'; snapshot: Record<string, string> }
  | { type: 'SESSION_INVALID'; token: string };

/**
 * Ask any open tab for its authenticated session. Resolves with the session
 * snapshot, or null if no tab responds within {@link timeoutMs}. An already-open
 * tab answers over an in-memory channel almost instantly, so the timeout only
 * bites when there is genuinely no other authenticated tab.
 *
 * @param timeoutMs - How long to wait for a response before giving up.
 * @returns The auth snapshot to hand to {@link TokenManager.importSession}, or null.
 */
export function requestSharedSession(
  timeoutMs = 800,
): Promise<Record<string, string> | null> {
  if (typeof BroadcastChannel === 'undefined') return Promise.resolve(null);
  return new Promise((resolve) => {
    let channel: BroadcastChannel | null = new BroadcastChannel(CHANNEL_NAME);
    let settled = false;
    const finish = (value: Record<string, string> | null) => {
      if (settled) return;
      settled = true;
      channel?.close();
      channel = null;
      resolve(value);
    };
    channel.onmessage = (event: MessageEvent<SyncMessage>) => {
      if (event.data?.type === 'SESSION' && event.data.snapshot) {
        finish(event.data.snapshot);
      }
    };
    channel.postMessage({ type: 'REQUEST_SESSION' } as SyncMessage);
    window.setTimeout(() => finish(null), timeoutMs);
  });
}

/**
 * Serve this tab's session to any tab that requests it. Reads the current
 * session from TokenManager at request time (no stale closure), and only
 * responds when this tab is authenticated.
 *
 * @returns A cleanup function that closes the channel.
 */
export function serveSharedSession(): () => void {
  if (typeof BroadcastChannel === 'undefined') return () => {};
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.onmessage = (event: MessageEvent<SyncMessage>) => {
    // A sibling's token was just rejected by the server. If it is ours too,
    // drop it now rather than serving it back to the tab that just cleared
    // it (the reload loop), and go to the login page ourselves — the next
    // request would 401 anyway.
    if (event.data?.type === 'SESSION_INVALID' && event.data.token) {
      TokenManager.markTokenDead(event.data.token);
      if (TokenManager.getToken() === event.data.token) {
        TokenManager.clearAll();
        window.location.href = '/login';
      }
      return;
    }
    if (event.data?.type === 'REQUEST_SESSION') {
      const snapshot = TokenManager.exportSession();
      if (snapshot) {
        channel.postMessage({ type: 'SESSION', snapshot } as SyncMessage);
      }
    }
  };
  return () => channel.close();
}

/**
 * Tell every other tab that `token` has been rejected by the server, so none
 * of them serves it back over the handoff or keeps using it.
 *
 * @param token - the token the server just answered 401 to
 */
export function broadcastSessionInvalid(token: string | null | undefined): void {
  if (!token || typeof BroadcastChannel === 'undefined') return;
  const channel = new BroadcastChannel(CHANNEL_NAME);
  channel.postMessage({ type: 'SESSION_INVALID', token } as SyncMessage);
  channel.close();
}
